import {
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  ReceiptText,
  Search,
  UserRound,
  WalletCards,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import studentImage from '../../assets/personsitting.png';
import { getPublicAcademyBySlug, getStudentCourseSignups, getStudentTransactions } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { getHostnameContext } from '../../utils/hostname';

const STUDENT_LINKS = [
  { label: 'Dashboard', path: '/student/portal', icon: LayoutDashboard },
  { label: 'My Courses', path: '/student/courses', icon: BookOpen },
  { label: 'Classes', path: '/student/classes', icon: CalendarDays },
  { label: 'Attendance', path: '/student/attendance', icon: ClipboardCheck },
  { label: 'Invoices', path: '/student/invoices', icon: ReceiptText },
  { label: 'Fee Payments', path: '/student/fee-payments', icon: WalletCards },
  { label: 'My Profile', path: '/student/profile', icon: UserRound },
];

export function StudentSidebar({ academyName = 'Student workspace', onLogout }) {
  const { user } = useAuth();
  const location = useLocation();
  const [unreadInvoices, setUnreadInvoices] = useState(0);
  const isActive = (path) => location.pathname === path
    || (path === '/student/courses' && location.pathname.startsWith('/student/courses/'));

  useEffect(() => {
    let mounted = true;
    async function loadUnreadInvoices() {
      if (!user?.uid) return;
      try {
        const allSignups = await getStudentCourseSignups(user.uid);
        let scoped = allSignups;
        const hostnameContext = getHostnameContext();
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? allSignups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const tenantIds = [...new Set(scoped.map((signup) => signup.tenantId).filter(Boolean))];
        const groups = await Promise.all(tenantIds.map((tenantId) => {
          const tenantSignups = scoped.filter((signup) => signup.tenantId === tenantId);
          return getStudentTransactions(tenantId, user.uid, user.email || tenantSignups.find((signup) => signup.email || signup.authEmail)?.email || '', tenantSignups.map((signup) => signup.id));
        }));
        const readKey = `smartcampus:read-invoices:${user.uid}`;
        const readIds = new Set(JSON.parse(localStorage.getItem(readKey) || '[]'));
        const count = groups.flat().filter((record) => (record.type === 'invoice' || record.category === 'invoice') && !readIds.has(record.id)).length;
        if (mounted) setUnreadInvoices(count);
      } catch (error) {
        console.warn('Unable to load unread invoice count', error);
      }
    }
    loadUnreadInvoices();
    window.addEventListener('student-invoice-read', loadUnreadInvoices);
    return () => { mounted = false; window.removeEventListener('student-invoice-read', loadUnreadInvoices); };
  }, [user?.email, user?.uid]);

  return (
    <aside className="student-sidebar">
      <Link className="student-sidebar-brand" to="/student/portal" aria-label="Student dashboard">
        <span className="student-sidebar-wordmark">smart<span>campus</span></span>
        <small>{academyName}</small>
      </Link>

      <label className="student-sidebar-search">
        <Search size={18} />
        <input type="search" placeholder="Search" aria-label="Search student portal" />
      </label>

      <nav className="student-sidebar-nav" aria-label="Student portal navigation">
        {STUDENT_LINKS.map(({ label, path, icon: Icon }) => (
          <Link className={isActive(path) ? 'is-active' : ''} to={path} key={path}>
            <Icon size={19} strokeWidth={1.7} />
            <span>{label}</span>
            {label === 'Invoices' && unreadInvoices > 0 ? <b className="student-sidebar-unread-badge" aria-label={`${unreadInvoices} unread invoices`}>{unreadInvoices > 99 ? '99+' : unreadInvoices}</b> : null}
          </Link>
        ))}
      </nav>

      <div className="student-sidebar-promo">
        <img src={studentImage} alt="Student learning online" />
        <strong>Keep learning<br />on the go</strong>
        <Link to="/student/downloads" aria-label="Open downloads">→</Link>
      </div>

      <button className="student-sidebar-logout" type="button" onClick={onLogout}>
        <LogOut size={18} strokeWidth={1.7} />
        Log out
      </button>
    </aside>
  );
}
